import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Camera, Video, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { AIAdvisoryBadge } from "@/components/owner/AIAdvisoryBadge";
import {
  getChecklistMediaRecommendations,
  type TaskMediaRecommendation,
} from "@/lib/checklistMediaRecommendations";

interface ChecklistMediaRecommendationsPanelProps {
  propertyId: string;
  tasks: { taskText: string; room: string }[];
  onDone: () => void;
}

export function ChecklistMediaRecommendationsPanel({
  propertyId,
  tasks,
  onDone,
}: ChecklistMediaRecommendationsPanelProps) {
  const [phase, setPhase] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [recs, setRecs] = useState<TaskMediaRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const run = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const result = await getChecklistMediaRecommendations(propertyId, tasks);
      setRecs(result);
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
      setPhase("error");
    }
  }, [propertyId, tasks]);

  useEffect(() => {
    if (tasks.length > 0) void run();
  }, [run]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const byRoom = recs.reduce<Record<string, TaskMediaRecommendation[]>>((acc, r) => {
    (acc[r.room] ??= []).push(r);
    return acc;
  }, {});

  const videoCount = recs.filter((r) => r.mediaType === "video").length;
  const photoCount = recs.filter((r) => r.mediaType === "photo").length;

  if (phase === "idle" || phase === "loading") {
    return (
      <div className="space-y-4 py-8 text-center px-2">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <div>
          <p className="font-medium">Generating proof recommendations</p>
          <p className="text-sm text-muted-foreground mt-1">
            AI is reviewing your checklist to suggest photo vs video proof for each task.
          </p>
        </div>
        <AIAdvisoryBadge kind="checklist_media_recommendations" className="mx-auto" expanded />
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="space-y-4 px-1">
        <p className="text-sm text-destructive">{error}</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => void run()}>
            Retry
          </Button>
          <Button variant="ghost" size="sm" onClick={onDone}>
            Skip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 px-1 min-w-0">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-semibold">Proof recommendations</h2>
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          AI reviewed your checklist and suggests how cleaners should document each task. Use
          these as a guide when configuring photo and video requirements on your checklist.
        </p>
        <AIAdvisoryBadge kind="checklist_media_recommendations" className="mt-2" expanded />
      </div>

      <div className="flex gap-2 flex-wrap">
        <Badge variant="secondary" className="gap-1">
          <Camera className="h-3 w-3" /> {photoCount} photo tasks
        </Badge>
        <Badge variant="secondary" className="gap-1">
          <Video className="h-3 w-3" /> {videoCount} video tasks
        </Badge>
      </div>

      <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-1 -mr-1">
        {Object.entries(byRoom).map(([room, items]) => (
          <div key={room}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {room}
            </p>
            <div className="space-y-2">
              {items.map((rec) => {
                const key = `${room}::${rec.taskText}`;
                const isOpen = expanded.has(key);
                const hasSteps = rec.videoSteps && rec.videoSteps.length > 0;
                return (
                  <div key={key} className="rounded-lg border bg-card p-3 space-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      {rec.mediaType === "video" ? (
                        <Video className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className="flex-1 text-sm font-medium min-w-0 break-words">
                        {rec.taskText}
                      </span>
                      <Badge
                        variant={rec.mediaType === "video" ? "default" : "secondary"}
                        className="text-[10px] h-5 shrink-0"
                      >
                        {rec.mediaType === "video" ? "Video" : "Photo"}
                      </Badge>
                      {hasSteps && (
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          className="text-muted-foreground hover:text-foreground ml-1 shrink-0"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? "Hide steps" : "Show steps"}
                        >
                          {isOpen ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground pl-6 break-words">{rec.rationale}</p>
                    {isOpen && rec.videoSteps && (
                      <ol className="pl-6 mt-1 space-y-0.5 list-decimal list-inside">
                        {rec.videoSteps.map((step, i) => (
                          <li key={i} className="text-[11px] text-foreground break-words">
                            {step}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Button className="w-full" onClick={onDone}>
        Looks good — continue
      </Button>
    </div>
  );
}
